import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LeagueCreationData, TeamFormat, ValidationError } from '../../../types/league';
import { countries } from '../../../data/countries';

interface BowlingCenter {
  venue: string;
  city: string;
  country: string;
  leagueCount: number;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000/api';

interface BasicInfoStepProps {
  data: Partial<LeagueCreationData>;
  updateData: (data: Partial<LeagueCreationData>) => void;
  errors: ValidationError[];
  addError: (field: string, message: string) => void;
  clearErrors: () => void;
}

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({ 
  data, 
  updateData, 
  errors,
  addError,
  clearErrors 
}) => {
  const { t } = useTranslation();
  const [bowlingCenters, setBowlingCenters] = useState<BowlingCenter[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredCenters, setFilteredCenters] = useState<BowlingCenter[]>([]);
  const venueInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Fetch bowling centers on mount
  useEffect(() => {
    const fetchBowlingCenters = async () => {
      try {
        const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
        const response = await fetch(`${apiUrl}/bowling-centers/active`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        const json = await response.json();
        if (json.success) {
          setBowlingCenters(json.venues);
        }
      } catch (error) {
        console.error('Error fetching bowling centers:', error);
      }
    };

    fetchBowlingCenters();
  }, []);

  // Filter centers based on input
  useEffect(() => {
    if (data.venue && data.venue.length > 0) {
      const query = data.venue.toLowerCase();
      const filtered = bowlingCenters.filter(center =>
        center.venue?.toLowerCase().includes(query) ||
        center.city?.toLowerCase().includes(query)
      );
      setFilteredCenters(filtered);
    } else {
      setFilteredCenters(bowlingCenters);
    }
  }, [data.venue, bowlingCenters]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        venueInputRef.current &&
        !venueInputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (field: keyof LeagueCreationData, value: any) => {
    updateData({ [field]: value });
    // Clear error for this field when user starts typing
    if (errors.some(e => e.field === field)) {
      clearErrors();
    }
  };

  const handleDurationChange = (weeks: number) => {
    if (data.startDate && weeks > 0) {
      const startDate = new Date(data.startDate);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + (weeks * 7));
      
      // Format to YYYY-MM-DD for the date input
      const formattedEndDate = endDate.toISOString().split('T')[0];
      updateData({ endDate: formattedEndDate });
    }
  };

  const handleSelectBowlingCenter = (center: BowlingCenter) => {
    updateData({ 
      venue: center.venue,
      city: center.city,
      country: center.country
    });
    setShowSuggestions(false);
    if (errors.some(e => e.field === 'venue' || e.field === 'city' || e.field === 'country')) {
      clearErrors();
    }
  };

  const validateStep = () => {
    clearErrors();
    let isValid = true;

    if (!data.name || data.name.trim().length < 3) {
      addError('name', t('admin.createLeague.steps.basicInfoStep.validation.nameMin'));
      isValid = false;
    }

    if (!data.description || data.description.trim().length < 10) {
      addError('description', t('admin.createLeague.steps.basicInfoStep.validation.descMin'));
      isValid = false;
    }

    if (!data.venue || data.venue.trim().length < 2) {
      addError('venue', t('admin.createLeague.steps.basicInfoStep.validation.venueRequired'));
      isValid = false;
    }

    if (!data.city || data.city.trim().length < 2) {
      addError('city', t('admin.createLeague.steps.basicInfoStep.validation.cityRequired'));
      isValid = false;
    }

    if (!data.country) {
      addError('country', t('admin.createLeague.steps.basicInfoStep.validation.countryRequired'));
      isValid = false;
    }

    if (!data.startDate) {
      addError('startDate', t('admin.createLeague.steps.basicInfoStep.validation.startDateRequired'));
      isValid = false;
    }

    if (!data.endDate) {
      addError('endDate', t('admin.createLeague.steps.basicInfoStep.validation.endDateRequired'));
      isValid = false;
    }

    if (data.startDate && data.endDate && new Date(data.startDate) >= new Date(data.endDate)) {
      addError('endDate', t('admin.createLeague.steps.basicInfoStep.validation.endDateAfterStart'));
      isValid = false;
    }

    if (!data.teamFormat) {
      addError('teamFormat', t('admin.createLeague.steps.basicInfoStep.validation.teamFormatRequired'));
      isValid = false;
    }

    return isValid;
  };

  const getFieldError = (field: string) => {
    return errors.find(e => e.field === field)?.message;
  };

  const teamFormats: { value: TeamFormat; label: string; description: string }[] = [
    { value: 'individual', label: t('admin.createLeague.steps.basicInfoStep.teamFormats.individual.label'), description: t('admin.createLeague.steps.basicInfoStep.teamFormats.individual.description') },
    { value: 'duos', label: t('admin.createLeague.steps.basicInfoStep.teamFormats.duos.label'), description: t('admin.createLeague.steps.basicInfoStep.teamFormats.duos.description') },
    { value: 'trios', label: t('admin.createLeague.steps.basicInfoStep.teamFormats.trios.label'), description: t('admin.createLeague.steps.basicInfoStep.teamFormats.trios.description') },
    { value: 'squads', label: t('admin.createLeague.steps.basicInfoStep.teamFormats.squads.label'), description: t('admin.createLeague.steps.basicInfoStep.teamFormats.squads.description') },
    { value: 'fives', label: t('admin.createLeague.steps.basicInfoStep.teamFormats.fives.label'), description: t('admin.createLeague.steps.basicInfoStep.teamFormats.fives.description') }
  ];

  return (
    
    <div className="space-y-6">
      
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">{t('admin.createLeague.steps.basicInfoStep.title')}</h2>
        <p className="text-purple-200">
          {t('admin.createLeague.steps.basicInfoStep.subtitle')}
        </p>
      </div>

      {/* League Name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-purple-200 mb-2">
          {t('admin.createLeague.steps.basicInfoStep.leagueName')} *
        </label>
        <input
          id="name"
          type="text"
          value={data.name || ''}
          onChange={(e) => handleInputChange('name', e.target.value)}
          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          placeholder={t('admin.createLeague.steps.basicInfoStep.leagueNamePlaceholder')}
        />
        {getFieldError('name') && (
          <p className="text-red-400 text-sm mt-1">{getFieldError('name')}</p>
        )}
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-purple-200 mb-2">
          {t('admin.createLeague.steps.basicInfoStep.description')} *
        </label>
        <textarea
          id="description"
          rows={4}
          value={data.description || ''}
          onChange={(e) => handleInputChange('description', e.target.value)}
          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          placeholder={t('admin.createLeague.steps.basicInfoStep.descriptionPlaceholder')}
        />
        {getFieldError('description') && (
          <p className="text-red-400 text-sm mt-1">{getFieldError('description')}</p>
        )}
      </div>

      {/* Venue Information */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="relative">
          <label htmlFor="venue" className="block text-sm font-medium text-purple-200 mb-2">
            {t('admin.createLeague.steps.basicInfoStep.venue')} *
          </label>
          <input
            ref={venueInputRef}
            id="venue"
            type="text"
            value={data.venue || ''}
            onChange={(e) => handleInputChange('venue', e.target.value)}
            onFocus={() => setShowSuggestions(true)}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder={t('admin.createLeague.steps.basicInfoStep.venuePlaceholder')}
            autoComplete="off"
          />
          {getFieldError('venue') && (
            <p className="text-red-400 text-sm mt-1">{getFieldError('venue')}</p>
          )}

          {/* Autocomplete Suggestions */}
          {showSuggestions && filteredCenters.length > 0 && (
            <div
              ref={suggestionsRef}
              className="absolute z-50 w-full mt-1 bg-gray-800 border border-purple-500/50 rounded-lg shadow-xl max-h-64 overflow-y-auto"
            >
              <div className="p-2 bg-purple-500/20 border-b border-purple-500/30">
                <p className="text-xs text-purple-200">
                  {t('admin.createLeague.steps.basicInfoStep.autocomplete.suggestedCenters', { count: filteredCenters.length })}
                </p>
              </div>
              {filteredCenters.map((center, index) => (
                <div
                  key={`${center.venue}-${index}`}
                  onClick={() => handleSelectBowlingCenter(center)}
                  className="px-4 py-3 hover:bg-purple-500/20 cursor-pointer border-b border-white/5 last:border-b-0 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-white font-medium">{center.venue}</p>
                      <p className="text-purple-200 text-sm">
                        {center.city}, {center.country}
                      </p>
                    </div>
                    <div className="ml-3">
                      <span className="inline-block px-2 py-1 bg-purple-500/30 text-purple-200 text-xs rounded">
                        {center.leagueCount} {center.leagueCount === 1 ? t('admin.createLeague.steps.basicInfoStep.autocomplete.leagueSingular') : t('admin.createLeague.steps.basicInfoStep.autocomplete.leaguePlural')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="city" className="block text-sm font-medium text-purple-200 mb-2">
            {t('admin.createLeague.steps.basicInfoStep.city')} *
          </label>
          <input
            id="city"
            type="text"
            value={data.city || ''}
            onChange={(e) => handleInputChange('city', e.target.value)}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder={t('admin.createLeague.steps.basicInfoStep.cityPlaceholder')}
          />
          {getFieldError('city') && (
            <p className="text-red-400 text-sm mt-1">{getFieldError('city')}</p>
          )}
        </div>
      </div>

      {/* Country */}
      <div>
        <label htmlFor="country" className="block text-sm font-medium text-purple-200 mb-2">
          {t('admin.createLeague.steps.basicInfoStep.country')} *
        </label>
        <select
          id="country"
          value={data.country || ''}
          onChange={(e) => handleInputChange('country', e.target.value)}
          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent [&>option]:bg-gray-800 [&>option]:text-white"
        >
          <option value="" className="bg-gray-800 text-white">{t('admin.createLeague.steps.basicInfoStep.countryPlaceholder')}</option>
          {countries.map((country) => (
            <option key={country.code} value={country.name} className="bg-gray-800 text-white">
              {country.name}
            </option>
          ))}
        </select>
        {getFieldError('country') && (
          <p className="text-red-400 text-sm mt-1">{getFieldError('country')}</p>
        )}
      </div>

      {/* League Dates */}
      <div className="grid md:grid-cols-3 gap-6">
        <div>
          <label htmlFor="startDate" className="block text-sm font-medium text-purple-200 mb-2">
            {t('admin.createLeague.steps.basicInfoStep.startDate')} *
          </label>
          <input
            id="startDate"
            type="date"
            value={data.startDate || ''}
            onChange={(e) => handleInputChange('startDate', e.target.value)}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          {getFieldError('startDate') && (
            <p className="text-red-400 text-sm mt-1">{getFieldError('startDate')}</p>
          )}
        </div>

        <div>
          <label htmlFor="durationWeeks" className="block text-sm font-medium text-purple-200 mb-2">
            <span className='text-xs'>{t('admin.createLeague.steps.basicInfoStep.durationWeeks')}</span>
          </label>
          <input
            id="durationWeeks"
            type="number"
            min="1"
            max="52"
            placeholder={t('admin.createLeague.steps.basicInfoStep.durationWeeksPlaceholder')}
            onChange={(e) => {
              const weeks = parseInt(e.target.value);
              if (!isNaN(weeks) && weeks > 0) {
                handleDurationChange(weeks);
              }
            }}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          <p className="text-purple-300 text-xs mt-1">{t('admin.createLeague.steps.basicInfoStep.autoCalculate')}</p>
        </div>

        <div>
          <label htmlFor="endDate" className="block text-sm font-medium text-purple-200 mb-2">
            {t('admin.createLeague.steps.basicInfoStep.endDate')} *
          </label>
          <input
            id="endDate"
            type="date"
            value={data.endDate || ''}
            onChange={(e) => handleInputChange('endDate', e.target.value)}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          {getFieldError('endDate') && (
            <p className="text-red-400 text-sm mt-1">{getFieldError('endDate')}</p>
          )}
        </div>
      </div>

      {/* Team Format */}
      <div>
        <label className="block text-sm font-medium text-purple-200 mb-4">
          {t('admin.createLeague.steps.basicInfoStep.teamFormat')} *
        </label>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teamFormats.map((format) => (
            <div
              key={format.value}
              onClick={() => handleInputChange('teamFormat', format.value)}
              className={`p-4 rounded-lg border-2 cursor-pointer transition duration-200 ${
                data.teamFormat === format.value
                  ? 'border-purple-500 bg-purple-500/20'
                  : 'border-white/20 bg-white/5 hover:border-white/40 hover:bg-white/10'
              }`}
            >
              <div className="text-center">
                <h3 className="text-white font-semibold mb-1">{format.label}</h3>
                <p className="text-purple-200 text-sm">{format.description}</p>
              </div>
            </div>
          ))}
        </div>
        {getFieldError('teamFormat') && (
          <p className="text-red-400 text-sm mt-2">{getFieldError('teamFormat')}</p>
        )}
      </div>

      {/* Validation Button - Hidden but used for form validation */}
      <button
        type="button"
        onClick={validateStep}
        className="hidden"
        id="validate-basic-info"
      >
        {t('admin.createLeague.steps.basicInfoStep.validate')}
      </button>
    </div>
  );
};

export default BasicInfoStep;